import { Input as InputPrimitive } from '@base-ui/react/input';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

const INPUT_TEXT_SIZE = {
  sm: 'text-xs/relaxed',
  default: 'text-sm md:text-xs/relaxed',
  lg: 'text-sm',
} as const;

const inputVariants = cva(
  'bg-field border-input focus-visible:border-ring focus-visible:ring-ring/30 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border transition-colors file:font-medium focus-visible:ring-2 aria-invalid:ring-2 file:text-foreground placeholder:text-muted-foreground w-full min-w-0 outline-none file:inline-flex file:border-0 file:bg-transparent disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        sm: `h-7 px-2 py-0.5 ${INPUT_TEXT_SIZE.sm} file:h-5 file:text-[0.625rem]`,
        default: `h-9 px-3 py-1 ${INPUT_TEXT_SIZE.default} file:h-6 file:text-xs/relaxed`,
        lg: `h-10 px-3 py-2 ${INPUT_TEXT_SIZE.lg} file:h-7 file:text-sm`,
      },
    },
    defaultVariants: { size: 'default' },
  },
);

const inputWrapperVariants = cva(
  'bg-field border-input focus-within:border-ring focus-within:ring-ring/30 rounded-lg border transition-colors focus-within:ring-2 w-full min-w-0 relative flex items-center has-[:disabled]:pointer-events-none has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 has-[:aria-invalid]:ring-destructive/20 dark:has-[:aria-invalid]:ring-destructive/40 has-[:aria-invalid]:border-destructive has-[:aria-invalid]:ring-2',
  {
    variants: {
      size: {
        sm: 'h-7 [&>[data-slot=input-icon]]:mx-1.5 [&>[data-slot=input-icon]_svg]:size-3',
        default: 'h-9 [&>[data-slot=input-icon]]:mx-2 [&>[data-slot=input-icon]_svg]:size-3.5',
        lg: 'h-10 [&>[data-slot=input-icon]]:mx-2.5 [&>[data-slot=input-icon]_svg]:size-4',
      },
    },
    defaultVariants: { size: 'default' },
  },
);

type InputProps = Omit<React.ComponentProps<'input'>, 'size'> &
  VariantProps<typeof inputVariants> & {
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
  };

// gloss
function Input({ className, type, size = 'default', startIcon, endIcon, ...props }: InputProps) {
  const hasIcons = startIcon || endIcon;

  if (!hasIcons) {
    return (
      <InputPrimitive type={type} data-slot="input" className={cn(inputVariants({ size, className }))} {...props} />
    );
  }

  return (
    <div data-slot="input" className={cn(inputWrapperVariants({ size, className }))}>
      {startIcon && (
        <span data-slot="input-icon" className="pointer-events-none shrink-0 text-muted-foreground">
          {startIcon}
        </span>
      )}
      <InputPrimitive
        type={type}
        data-slot="input-field"
        className={cn(
          'h-full w-full min-w-0 border-0 bg-transparent p-0 text-inherit outline-none file:inline-flex file:border-0 file:bg-transparent file:font-medium file:text-foreground placeholder:text-muted-foreground focus:shadow-none focus:ring-0 disabled:pointer-events-none disabled:cursor-not-allowed',
          INPUT_TEXT_SIZE[size ?? 'default'],
          !startIcon && (size === 'sm' ? 'pl-2' : 'pl-3'),
          !endIcon && (size === 'sm' ? 'pr-2' : 'pr-3'),
        )}
        {...props}
      />
      {endIcon && (
        <span data-slot="input-icon" className="pointer-events-none shrink-0 text-muted-foreground">
          {endIcon}
        </span>
      )}
    </div>
  );
}

export { Input, inputVariants, inputWrapperVariants };
